/**
 * Comma-separated values, for a teacher taking answers out of the console.
 *
 * 🔴 A SPREADSHEET EXECUTES A CELL THAT STARTS WITH `=`, `+`, `-` OR `@`. Every answer in this
 * file is free text a student typed, so an answer beginning `=HYPERLINK(...)` or
 * `=cmd|'/c calc'!A1` becomes a formula the moment a teacher opens the download — the CSV
 * injection class, and the reason this is a module with tests rather than a `join(",")` at a
 * call site. The value is preserved exactly and prefixed with an apostrophe, which spreadsheets
 * strip on display and which makes the cell text rather than code.
 * ⚠️ A LEADING TAB OR CARRIAGE RETURN COUNTS TOO — both let a formula through in Excel, and
 * both survive `trim()` on the way in from a textarea.
 */
const RISKY = new Set(["=", "+", "-", "@", "\t", "\r"]);

export function cell(value: unknown): string {
  const s = value === null || value === undefined ? "" : String(value);
  // 🔴 NEUTRALISE FIRST, QUOTE SECOND. The apostrophe has to be inside the quoted field, or
  // it is a stray character between two commas rather than part of the value.
  const safe = s !== "" && RISKY.has(s[0]!) ? `'${s}` : s;
  // ⚠️ RFC 4180: a field containing a comma, a quote or a newline is quoted, and a quote
  // inside it is doubled. A field that needs none of that is written bare.
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** One sheet: a header row and the rows under it, CRLF as the spec requires. */
export function toCsv(header: string[], rows: unknown[][]): string {
  return [header, ...rows].map((r) => r.map(cell).join(",")).join("\r\n");
}

/**
 * Hand the file to the browser.
 *
 * ⚠️ THE BYTE-ORDER MARK IS NOT DECORATION. Excel reads a CSV without one as the system code
 * page, so an accented name or an em-dash arrives mojibake — and the people most likely to be
 * mangled are the ones whose names are not ASCII.
 */
export function download(filename: string, csv: string): void {
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Released on the next tick: revoking synchronously can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A filename somebody can find again: the cohort, the round, and the day. */
export function stamped(cohort: string, what: string, on = new Date()): string {
  const slug = cohort.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
    || "cohort";
  const day = `${on.getFullYear()}-${String(on.getMonth() + 1).padStart(2, "0")}-`
    + String(on.getDate()).padStart(2, "0");
  return `${slug}-${what}-${day}.csv`;
}
